import { Icon } from "@iconify/react";
import React from "react";
import { Link } from "react-router-dom";
import "../internal/YouWoConsumerLanding.css";
import ModernFooter from "../../components/landing/modern/ModernFooter";
import SEOHead from "../../components/tools/SEOHead";
import ToolsLandingNav from "../../components/tools/ToolsLandingNav";
import "../../components/tools/ToolsLandingShell.css";
import { buildAppLink } from "../../utils/landingRedirect";
import "./ComparePages.css";

const comparisonRows = [
  {
    need: "General concept explanation",
    chatgpt: "Strong fit",
    youwo: "Strong when tied to your course sources",
    chatgptIcon: "lucide:check",
    youwoIcon: "lucide:check",
  },
  {
    need: "Lecture recording workflow",
    chatgpt: "Requires manual setup",
    youwo: "Core workflow",
    chatgptIcon: "lucide:settings",
    youwoIcon: "lucide:check",
  },
  {
    need: "PDF page references",
    chatgpt: "Possible in supported workflows",
    youwo: "Built into source-cited study output",
    chatgptIcon: "lucide:file-search",
    youwoIcon: "lucide:check",
  },
  {
    need: "Video timestamps",
    chatgpt: "Depends on transcript setup",
    youwo: "Designed for timestamped learning",
    chatgptIcon: "lucide:clock",
    youwoIcon: "lucide:check",
  },
  {
    need: "Quizzes and flashcards from class material",
    chatgpt: "Works with enough prompting",
    youwo: "Dedicated study workflow",
    chatgptIcon: "lucide:message-square-text",
    youwoIcon: "lucide:check",
  },
  {
    need: "Course source workspace",
    chatgpt: "Flexible chat workspace",
    youwo: "Lecture, PDF, video, note, and chat context together",
    chatgptIcon: "lucide:layout-dashboard",
    youwoIcon: "lucide:check",
  },
];

const faqItems = [
  {
    question: "Is YouWo a ChatGPT replacement for students?",
    answer:
      "Not always. ChatGPT is useful for broad explanations and brainstorming. YouWo is better when the answer needs to come from your lecture, PDF, video, or notes and you want source references you can verify.",
  },
  {
    question: "Can ChatGPT use files and citations?",
    answer:
      "Yes, ChatGPT supports file workflows in supported plans and modes, and some workflows can include citations or source links. The practical difference is that YouWo is built around the student source workflow from the start.",
  },
  {
    question: "What makes YouWo better for class-specific studying?",
    answer:
      "YouWo organizes recordings, PDFs, videos, notes, chats, quizzes, and flashcards around the student's own sources. That makes it easier to ask what the professor said, return to a timestamp, and review the source before an exam.",
  },
  {
    question: "Does YouWo do homework for students?",
    answer:
      "YouWo is positioned for learning, review, source understanding, and study practice. It should help students understand their material and produce study outputs from sources, not bypass academic integrity rules.",
  },
  {
    question: "Can I use both ChatGPT and YouWo?",
    answer:
      "Yes. Many students can use ChatGPT for general explanations and YouWo for source-cited studying from lectures, PDFs, videos, and notes.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "YouWo vs ChatGPT for Studying",
      url: "https://youwo.ai/compare/youwo-vs-chatgpt-for-studying",
      description:
        "A fair comparison of ChatGPT and YouWo for students who need source-cited answers from lectures, PDFs, videos, and notes.",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Tools",
          item: "https://youwo.ai/tools",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "YouWo vs ChatGPT for Studying",
          item: "https://youwo.ai/compare/youwo-vs-chatgpt-for-studying",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
  ],
};

const YouWoVsChatGPTForStudying: React.FC = () => (
  <>
    <SEOHead
      title="YouWo vs ChatGPT for Studying - Source-Cited AI for Lectures, PDFs, and Videos"
      description="Compare YouWo and ChatGPT for studying. Learn when generic AI chat is enough and when students need source-cited answers, lecture timestamps, quizzes, and flashcards."
      keywords="ChatGPT for studying, YouWo vs ChatGPT for studying, ChatGPT alternative for students, source cited AI study assistant, AI study tool with citations, AI for lecture notes and PDFs"
      canonicalUrl="https://youwo.ai/compare/youwo-vs-chatgpt-for-studying"
      structuredData={structuredData}
    />

    <main className="yw-consumer-page yw-tools-page yw-tools-shell yw-compare-page" data-lang="en" data-variant="compare-chatgpt">
      <ToolsLandingNav
        links={[
          { label: "Comparison", href: "#comparison" },
          { label: "Demo", href: "#demo" },
          { label: "FAQ", href: "#faq" },
        ]}
        ctaLabel="Study in YouWo"
      />

      <section className="yw-tools-hero">
        <div className="yw-tools-hero-copy">
          <p className="yw-tools-kicker">
            <Icon icon="lucide:scale" />
            Fair student workflow comparison
          </p>
          <h1>
            YouWo vs ChatGPT for <span>source-cited studying</span>
          </h1>
          <p>
            ChatGPT is useful for explanations and brainstorming. YouWo is built for the moment a student needs answers from an actual lecture, PDF, video, or note with a source path they can check.
          </p>
          <div className="yw-tools-hero-actions">
            <a className="yw-tools-primary-link" href={buildAppLink()} target="_blank" rel="noopener noreferrer">
              Study with your own sources
              <Icon icon="lucide:arrow-up-right" />
            </a>
            <Link className="yw-tools-secondary-link" to="/tools/chatgpt-for-students">
              View student AI tools
              <Icon icon="lucide:arrow-right" />
            </Link>
          </div>
        </div>

        <aside className="yw-compare-scoreboard" aria-label="Short answer">
          <div className="yw-compare-score-inner">
            <div className="yw-compare-score-card">
              <h2>Use ChatGPT when</h2>
              <p>You want a fast general explanation, a brainstorm, a rewrite, or a first-pass summary.</p>
              <div className="yw-compare-pill-row">
                <span className="yw-compare-pill"><Icon icon="lucide:lightbulb" /> Explanations</span>
                <span className="yw-compare-pill"><Icon icon="lucide:pen-line" /> Drafting</span>
                <span className="yw-compare-pill"><Icon icon="lucide:sparkles" /> Ideas</span>
              </div>
            </div>
            <div className="yw-compare-score-card yw-compare-score-card-primary">
              <h3>Use YouWo when</h3>
              <p>The answer must match your class material and point back to the lecture timestamp, PDF page, video moment, or saved note.</p>
              <div className="yw-compare-pill-row">
                <span className="yw-compare-pill"><Icon icon="lucide:mic" /> Lectures</span>
                <span className="yw-compare-pill"><Icon icon="lucide:file-text" /> PDFs</span>
                <span className="yw-compare-pill"><Icon icon="lucide:quote" /> Citations</span>
              </div>
            </div>
          </div>
        </aside>
      </section>

      <section className="yw-tools-section" id="comparison">
        <div className="yw-tools-section-heading">
          <p className="yw-compare-section-eyebrow">
            <Icon icon="lucide:book-open-check" />
            The real study question
          </p>
          <h2>Can the AI answer from your actual class sources?</h2>
          <p>
            A generic explanation can be correct and still miss what your professor emphasized. For studying, the useful output is grounded in the material you will be tested on.
          </p>
        </div>

        <div className="yw-compare-article">
          <p>
            Students already have the source context: lecture recordings, readings, slide decks, YouTube lectures, notes, images, transcripts, and study guides. The hard part is turning that scattered material into a reliable study system.
          </p>
          <ul>
            <li><Icon icon="lucide:check" /> Ask questions across the lecture, PDF, video, and notes.</li>
            <li><Icon icon="lucide:check" /> Get answers that point back to source evidence.</li>
            <li><Icon icon="lucide:check" /> Turn those sources into study guides, quizzes, and flashcards.</li>
          </ul>
        </div>

        <div className="yw-seo-comparison-card yw-compare-table" aria-label="ChatGPT and YouWo comparison table">
          <div className="yw-seo-comparison-head">
            <span>Study need</span>
            <span>ChatGPT</span>
            <span>YouWo</span>
          </div>
          {comparisonRows.map((row) => (
            <div className="yw-seo-comparison-row" key={row.need}>
              <strong>{row.need}</strong>
              <span className="yw-compare-table-value yw-compare-table-value-muted">
                <Icon icon={row.chatgptIcon} />
                {row.chatgpt}
              </span>
              <span className="yw-compare-table-value">
                <Icon icon={row.youwoIcon} />
                {row.youwo}
              </span>
            </div>
          ))}
        </div>

        <div className="yw-compare-fair-note">
          <h3>Fair comparison note</h3>
          <p>
            ChatGPT supports file uploads in supported workflows and OpenAI describes Deep Research modes that can include citations or source links. OpenAI also recommends verifying important information. YouWo's position is narrower: source-cited student work from lectures, PDFs, videos, and notes as the default workflow.
          </p>
          <p>
            Sources:{" "}
            <a href="https://help.openai.com/en/articles/8555545-file-uploads-faq" target="_blank" rel="noopener noreferrer">OpenAI file uploads FAQ</a>,{" "}
            <a href="https://help.openai.com/en/articles/10500283-deep-research-in-chatgpt" target="_blank" rel="noopener noreferrer">OpenAI Deep Research help</a>, and{" "}
            <a href="https://help.openai.com/en/articles/8313428-does-chatgpt-tell-the-truth" target="_blank" rel="noopener noreferrer">OpenAI truthfulness guidance</a>.
          </p>
        </div>
      </section>

      <section className="yw-tools-section" id="demo">
        <div className="yw-tools-section-heading">
          <p className="yw-compare-section-eyebrow">
            <Icon icon="lucide:play-circle" />
            Product demo
          </p>
          <h2>Same question, different study output.</h2>
          <p>
            Sample scenario: a psychology student has a lecture recording, a PDF chapter, a YouTube review video, and their own notes. The question is simple: what are retrieval cues and why do they matter?
          </p>
        </div>

        <div className="yw-compare-source-grid">
          <div className="yw-compare-card">
            <h3>Lecture recording</h3>
            <p>45-minute class recording with the professor's examples and exam emphasis.</p>
            <ul>
              <li><Icon icon="lucide:clock" /> Key moment at 18:42</li>
            </ul>
          </div>
          <div className="yw-compare-card">
            <h3>PDF chapter</h3>
            <p>Assigned reading with formal definitions and diagrams.</p>
            <ul>
              <li><Icon icon="lucide:file-text" /> Definition on page 12</li>
            </ul>
          </div>
          <div className="yw-compare-card">
            <h3>Video review</h3>
            <p>Supplemental video explaining the same idea with a worked example.</p>
            <ul>
              <li><Icon icon="lucide:youtube" /> Example at 06:17</li>
            </ul>
          </div>
        </div>

        <div className="yw-compare-demo-grid">
          <div className="yw-compare-output-card yw-compare-output-card-weak">
            <span className="yw-compare-output-label">
              <Icon icon="lucide:message-square-text" />
              Weak generic answer
            </span>
            <h3>Sounds useful, but hard to verify</h3>
            <p className="yw-compare-quote">
              Retrieval cues are reminders that help trigger memories. They matter because they make it easier to remember information later, especially when the cue is related to how the memory was encoded.
            </p>
            <ul>
              <li><Icon icon="lucide:x" /> No lecture timestamp.</li>
              <li><Icon icon="lucide:x" /> No PDF page reference.</li>
              <li><Icon icon="lucide:x" /> No way to check whether this matches the class emphasis.</li>
            </ul>
          </div>

          <div className="yw-compare-output-card yw-compare-output-card-strong">
            <span className="yw-compare-output-label">
              <Icon icon="lucide:quote" />
              YouWo-style source-cited answer
            </span>
            <h3>Useful because it points back to the source</h3>
            <p className="yw-compare-quote">
              Retrieval cues help recall by reconnecting the test situation to the original encoding context. In lecture, the professor used the coffee shop example to show how place, smell, and routine can trigger a memory. The PDF defines this as encoding specificity, and the review video connects it to practice questions.
            </p>
            <div className="yw-compare-citations" aria-label="Example source citations">
              <span className="yw-compare-citation"><Icon icon="lucide:clock" /> Lecture 18:42</span>
              <span className="yw-compare-citation"><Icon icon="lucide:file-text" /> PDF p. 12</span>
              <span className="yw-compare-citation"><Icon icon="lucide:youtube" /> Video 06:17</span>
            </div>
          </div>
        </div>
      </section>

      <section className="yw-tools-section">
        <div className="yw-tools-section-heading">
          <p className="yw-compare-section-eyebrow">
            <Icon icon="lucide:route" />
            Choosing the right tool
          </p>
          <h2>Use the tool that matches the job.</h2>
          <p>
            The best study workflow is not one AI for everything. It is using general AI for broad thinking and source-cited AI when the answer needs evidence.
          </p>
        </div>

        <div className="yw-compare-fit-grid">
          <div className="yw-compare-card">
            <h3>ChatGPT is a good fit for</h3>
            <ul>
              <li><Icon icon="lucide:check" /> Explaining a concept in simpler language.</li>
              <li><Icon icon="lucide:check" /> Brainstorming examples or essay angles.</li>
              <li><Icon icon="lucide:check" /> Rewriting confusing notes.</li>
              <li><Icon icon="lucide:check" /> First-pass summaries and general practice.</li>
            </ul>
          </div>
          <div className="yw-compare-card">
            <h3>YouWo is a good fit for</h3>
            <ul>
              <li><Icon icon="lucide:check" /> Studying from lecture recordings and transcripts.</li>
              <li><Icon icon="lucide:check" /> Asking questions across PDFs, videos, and notes.</li>
              <li><Icon icon="lucide:check" /> Creating study guides, quizzes, and flashcards from sources.</li>
              <li><Icon icon="lucide:check" /> Verifying answers with timestamps and page references.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="yw-tools-section" id="faq">
        <div className="yw-tools-section-heading">
          <p className="yw-compare-section-eyebrow">
            <Icon icon="lucide:help-circle" />
            FAQ
          </p>
          <h2>Common questions about YouWo and ChatGPT for studying.</h2>
        </div>

        <div className="yw-compare-faq-list">
          {faqItems.map((item) => (
            <article className="yw-compare-faq-item" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="yw-tools-final-cta">
        <div>
          <h2>Turn your lectures, PDFs, videos, and notes into study output you can verify.</h2>
          <p>Open YouWo to keep sources connected, ask follow-up questions, and create study materials from the content your class actually uses.</p>
          <a href={buildAppLink()} target="_blank" rel="noopener noreferrer">
            Study with your own sources
            <Icon icon="lucide:arrow-up-right" />
          </a>
        </div>
      </section>
    </main>

    <div className="yw-tools-footer-shell">
      <ModernFooter />
    </div>
  </>
);

export default YouWoVsChatGPTForStudying;
